if (!customElements.get("custom-auto-assign-gwp")) {
  customElements.define(
    "custom-auto-assign-gwp",
    class CustomAutoAssignGwp extends HTMLElement {
      static GWP_PROPERTY = "_gwp";
      static GWP_PROPERTY_VALUE = "true";

      constructor() {
        super();
        this._gwpPromotions = [];
      }

      connectedCallback() {
        this._gwpPromotions = this._parseGwpData(
          this.getAttribute("data-gwp-promotions"),
        );
      }

      _parseGwpData(data) {
        if (!data || !data.trim()) return [];
        const items = [];
        const pairs = data.split(",");
        for (const pair of pairs) {
          const trimmed = pair.trim();
          if (!trimmed) continue;
          const parts = trimmed.split(":");
          if (parts.length === 2) {
            const variantId = parts[0].trim();
            const quantity = parseInt(parts[1], 10);
            if (variantId && !isNaN(quantity)) {
              items.push({ variantId, quantity });
            }
          }
        }
        return items;
      }

      get gwpPromotions() {
        return this._gwpPromotions || [];
      }

      get sectionId() {
        return this.getAttribute("data-section-id");
      }

      isAutoGwpItem(item) {
        return (
          item?.properties?.[CustomAutoAssignGwp.GWP_PROPERTY] ===
          CustomAutoAssignGwp.GWP_PROPERTY_VALUE
        );
      }

      async refetchSection() {
        if (!this.sectionId) return;
        try {
          const response = await fetch(
            `${window.location.pathname}?section_id=${this.sectionId}`,
          );
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");
          const newElement = doc.querySelector("custom-auto-assign-gwp");
          if (newElement) {
            const newData = newElement.getAttribute("data-gwp-promotions");
            this._gwpPromotions = this._parseGwpData(newData);
          }
        } catch (e) {
          console.error("Failed to refetch GWP section:", e);
        }
      }

      _getEligibleGwpMap() {
        const shouldHave = new Map();
        this._gwpPromotions.forEach((gwp) => {
          const current = shouldHave.get(gwp.variantId) || 0;
          shouldHave.set(gwp.variantId, current + gwp.quantity);
        });
        return shouldHave;
      }

      _buildReconciliationPlan(cartItems = []) {
        const shouldHave = this._getEligibleGwpMap();
        const autoGwpLines = cartItems.filter((item) =>
          this.isAutoGwpItem(item),
        );
        const autoByVariant = new Map();

        autoGwpLines.forEach((item) => {
          const variantId = item.variant_id.toString();
          if (!autoByVariant.has(variantId)) {
            autoByVariant.set(variantId, []);
          }
          autoByVariant.get(variantId).push(item);
        });

        const itemsToAdd = [];
        const lineChanges = [];

        shouldHave.forEach((quantity, variantId) => {
          const existingLines = autoByVariant.get(variantId) || [];

          if (existingLines.length === 0) {
            itemsToAdd.push({
              id: Number(variantId),
              quantity,
              properties: {
                [CustomAutoAssignGwp.GWP_PROPERTY]:
                  CustomAutoAssignGwp.GWP_PROPERTY_VALUE,
              },
            });
            return;
          }

          const [primaryLine, ...extraLines] = existingLines;
          if (primaryLine.quantity !== quantity) {
            lineChanges.push({
              id: primaryLine.key,
              quantity,
            });
          }

          // Keep a single auto-GWP line per variant; drop duplicates.
          extraLines.forEach((line) => {
            lineChanges.push({
              id: line.key,
              quantity: 0,
            });
          });
        });

        autoByVariant.forEach((lines, variantId) => {
          if (shouldHave.has(variantId)) return;
          lines.forEach((line) => {
            lineChanges.push({
              id: line.key,
              quantity: 0,
            });
          });
        });

        return { itemsToAdd, lineChanges };
      }

      async _fetchCartWithSections(sections, sectionsUrl) {
        const cartResponse = await fetch(`${routes.cart_url}.js`);
        const cart = await cartResponse.json();

        if (sections?.length) {
          const url = new URL(
            sectionsUrl || window.location.pathname,
            window.location.origin,
          );
          url.searchParams.set("sections", sections.join(","));
          const sectionsResponse = await fetch(
            `${url.pathname}${url.search}${url.hash}`,
          );
          cart.sections = await sectionsResponse.json();
        }

        return cart;
      }

      /**
       * Reconcile auto-assigned GWP lines using `_gwp` properties.
       * Returns the updated cart (with optional sections) when mutations ran,
       * or null when the cart already matches eligibility.
       */
      async reconcileGwp(cartItems = [], options = {}) {
        const { sections, sections_url: sectionsUrl } = options;
        const { itemsToAdd, lineChanges } =
          this._buildReconciliationPlan(cartItems);

        if (!itemsToAdd.length && !lineChanges.length) {
          return null;
        }

        for (const change of lineChanges) {
          const response = await fetch(`${routes.cart_change_url}.js`, {
            ...fetchConfig(),
            body: JSON.stringify({
              id: change.id,
              quantity: change.quantity,
            }),
          });
          await response.json();
        }

        if (itemsToAdd.length) {
          const response = await fetch(`${routes.cart_add_url}.js`, {
            ...fetchConfig(),
            body: JSON.stringify({ items: itemsToAdd }),
          });
          await response.json();
        }

        return this._fetchCartWithSections(sections, sectionsUrl);
      }
    },
  );
}
